import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Plane } from "lucide-react";
import { useTrips, useDeleteTrip } from "../../application/hooks/useTrips";
import { useConfirm } from "@tabipla/components";
import { Badge } from "@tabipla/ui";
import { useAdsInList, NativeAd, useAdContext } from "@tabipla/components";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { LoadingState } from "../components/LoadingState";
import { ErrorState } from "../components/ErrorState";
import { TripCard } from "../components/TripCard";
import { EmptyState } from "../components/EmptyState";
import { CreateTripDialog } from "../components/CreateTripDialog";
import { computeTripStatus } from "@tabipla/shared";

const AD_SLOT_TRIPS = import.meta.env.VITE_AD_SLOT_TRIPS ?? "0000000000";

type FilterStatus = "all" | "planning" | "confirmed" | "completed";

const FILTER_OPTIONS: { value: FilterStatus; labelKey: string }[] = [
  { value: "all", labelKey: "trips.filterAll" },
  { value: "planning", labelKey: "trips.statusPlanning" },
  { value: "confirmed", labelKey: "trips.statusConfirmed" },
  { value: "completed", labelKey: "trips.statusCompleted" },
];

export function Trips() {
  const { t } = useTranslation();
  const { data: trips, isLoading, error, refetch } = useTrips();
  const deleteMutation = useDeleteTrip();
  const { confirm, ConfirmDialog } = useConfirm();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const adContext = useAdContext();

  const filteredTrips = useMemo(() => {
    if (!trips) return [];

    return trips.filter((trip) => {
      // Compute status based on dates
      const computedStatus = computeTripStatus(trip.startDate, trip.endDate);

      // Status filter
      if (filterStatus !== "all" && computedStatus !== filterStatus) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          trip.title.toLowerCase().includes(query) || trip.destination.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [trips, filterStatus, searchQuery]);

  // 広告をリストに挿入
  const tripsWithAds = useAdsInList(filteredTrips, {
    interval: 6,
    startAfter: 5,
    maxAds: 3,
    slotIds: [AD_SLOT_TRIPS],
    enabled: adContext?.config.enabled ?? false,
  });

  const handleDelete = async (tripId: string) => {
    const confirmed = await confirm({
      title: t("trips.deleteTitle", "旅行を削除"),
      description: t("trips.deleteConfirm"),
      confirmText: t("common.delete", "削除"),
      variant: "destructive",
    });

    if (confirmed) {
      try {
        await deleteMutation.mutateAsync(tripId);
        toast.success(t("trips.deleteSuccess", "旅行を削除しました"));
      } catch {
        toast.error(t("trips.deleteError", "削除に失敗しました"));
      }
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout title={t("trips.title")}>
        <LoadingState message={t("trips.loading")} />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title={t("trips.title")}>
        <ErrorState error={error} onRetry={refetch} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={t("trips.title")}
      showSearch
      searchPlaceholder={t("trips.searchPlaceholder", "旅行を検索...")}
      onSearch={setSearchQuery}
      fabAction={{
        icon: Plus,
        label: t("trips.createNew"),
        onClick: () => setShowCreateDialog(true),
      }}
    >
      <div className="animate-fade-in space-y-6">
        {/* Header */}
        <div className="hidden lg:block">
          <h1 className="text-2xl font-bold tracking-tight">{t("trips.title")}</h1>
          <p className="text-muted-foreground">{t("trips.subtitle")}</p>
        </div>

        {/* Filter Pills */}
        <div className="scrollbar-hide flex gap-2 overflow-x-auto pb-2">
          {FILTER_OPTIONS.map((option) => (
            <Badge
              key={option.value}
              variant={filterStatus === option.value ? "default" : "outline"}
              className="cursor-pointer whitespace-nowrap px-4 py-2 text-sm transition-all hover:scale-105 active:scale-95"
              onClick={() => setFilterStatus(option.value)}
            >
              {t(option.labelKey)}
            </Badge>
          ))}
        </div>

        {/* Trip Grid */}
        {trips && trips.length === 0 ? (
          <EmptyState
            icon={Plane}
            title={t("trips.noTrips")}
            description={t("trips.noTripsDescription", "最初の旅行を計画してみましょう")}
            action={{
              label: t("trips.createNew"),
              onClick: () => setShowCreateDialog(true),
              icon: Plus,
            }}
          />
        ) : filteredTrips.length === 0 ? (
          <EmptyState
            icon={Plane}
            title={t("trips.noMatchingTrips", "該当する旅行がありません")}
            description={t("trips.tryDifferentFilter", "フィルターを変更してみてください")}
            variant="dashed"
          />
        ) : (
          <div className="space-y-3">
            {tripsWithAds.map((item) =>
              item.type === "ad" ? (
                <NativeAd key={item.key} slotId={item.slotId} variant="card" />
              ) : (
                <TripCard key={item.data.id} trip={item.data} onDelete={handleDelete} />
              )
            )}
          </div>
        )}
      </div>

      <CreateTripDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
      <ConfirmDialog />
    </DashboardLayout>
  );
}
